import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Activity, Receipt, ShieldCheck, Wallet, Plus, ArrowRight, Command, MessageSquareHeart, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Spotlight-style command palette for the clinic portal (/portal).
 *
 * Differs from the Admin CommandPalette in two ways:
 *   1. Built-in "Quick actions" list (Send a case, View invoices, etc.) so a brand-new
 *      user with no cases still gets value out of ⌘K.
 *   2. Case search uses GET /api/my/cases (own cases only) — no admin search endpoint.
 *
 * Opens with Ctrl/Cmd+K from anywhere inside /portal.
 */

const QUICK_ACTIONS = [
    { key: "send_case", icon: Plus, label: "Send a new case", sublabel: "Open the case submission form", to: "/workflow#send-case" },
    { key: "my_cases", icon: Activity, label: "My cases", sublabel: "Live status of every case you've sent", to: "/portal?tab=cases" },
    { key: "invoices", icon: Receipt, label: "Invoices", sublabel: "Open invoices · pay online · download PDF", to: "/portal?tab=invoices" },
    { key: "credits", icon: Wallet, label: "Credits", sublabel: "Top-up balance · auto-top-up · recent transactions", to: "/portal?tab=credits" },
    { key: "warranty", icon: ShieldCheck, label: "Warranty cards", sublabel: "Download the 10-year clinical warranty PDF for delivered cases", to: "/portal?tab=warranty" },
    { key: "feedback", icon: MessageSquareHeart, label: "Send feedback", sublabel: "Tell us what's working — and what isn't", to: "/feedback" },
];

export default function ClientCommandPalette() {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [myCases, setMyCases] = useState([]);
    const [loading, setLoading] = useState(false);
    const [active, setActive] = useState(0);
    const inputRef = useRef(null);
    const nav = useNavigate();

    useEffect(() => {
        const onKey = (e) => {
            const isMac = navigator.platform.toLowerCase().includes("mac");
            const ctrl = isMac ? e.metaKey : e.ctrlKey;
            if (ctrl && (e.key === "k" || e.key === "K")) {
                e.preventDefault();
                setOpen((o) => !o);
            } else if (e.key === "Escape" && open) {
                setOpen(false);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.focus(), 50);
            setActive(0);
            // Prefetch the client's own cases — fast since clinics rarely have huge case histories.
            setLoading(true);
            axios.get(`${API}/my/cases`)
                .then(({ data }) => setMyCases(Array.isArray(data) ? data : []))
                .catch(() => setMyCases([]))
                .finally(() => setLoading(false));
        } else {
            setQ("");
        }
    }, [open]);

    // Build the visible list: filtered quick actions + matching cases.
    const term = q.trim().toLowerCase();
    const filteredActions = QUICK_ACTIONS.filter((a) =>
        !term || a.label.toLowerCase().includes(term) || a.sublabel.toLowerCase().includes(term),
    );
    const filteredCases = term.length >= 2
        ? myCases.filter((c) => {
            const hay = [c.case_number, c.product, c.shade, c.status, c.notes, c.patient_id]
                .filter(Boolean).join(" ").toLowerCase();
            return hay.includes(term);
        }).slice(0, 8)
        : myCases.slice(0, 4); // Show 4 most-recent cases when no query

    const items = [
        ...filteredActions.map((a) => ({ kind: "action", ...a })),
        ...filteredCases.map((c) => ({
            kind: "case",
            id: c.id,
            icon: Activity,
            label: c.case_number,
            sublabel: `${c.product?.replace(/-/g, " ") || ""} · ${c.status?.replace(/_/g, " ") || ""}`,
            to: `/cases/${c.id}`,
        })),
    ];

    const pick = useCallback((item) => {
        setOpen(false);
        if (item?.to) nav(item.to);
    }, [nav]);

    const onKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((a) => Math.min(items.length - 1, a + 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => Math.max(0, a - 1));
        } else if (e.key === "Enter" && items[active]) {
            e.preventDefault();
            pick(items[active]);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] px-4" data-testid="client-command-palette">
            <button className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" aria-label="Close" onClick={() => setOpen(false)} />
            <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-[tr-ccp-in_180ms_cubic-bezier(.22,1,.36,1)]">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
                    <Search size={16} className="text-slate-400" />
                    <input
                        ref={inputRef}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={onKeyDown}
                        placeholder="Type an action or case number… (e.g. 'send', 'invoice', 'TR-')"
                        data-testid="client-palette-input"
                        className="flex-1 bg-transparent outline-none text-[14px] placeholder-slate-400"
                    />
                    {loading ? <Loader2 size={14} className="animate-spin text-slate-400" /> : (
                        <kbd className="px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 font-mono-tr text-[10.5px] text-slate-500">ESC</kbd>
                    )}
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                    {items.length === 0 ? (
                        <div className="py-12 text-center text-[13px] text-slate-500">
                            <Command size={18} className="mx-auto text-slate-300 mb-2" />
                            No matches. Try <span className="font-mono-tr text-[11px]">send</span> or <span className="font-mono-tr text-[11px]">invoice</span>.
                        </div>
                    ) : (
                        <>
                            {filteredActions.length > 0 && (
                                <div className="px-3 pt-2 pb-1 font-mono-tr text-[10px] tracking-[0.22em] uppercase text-slate-400">Quick actions</div>
                            )}
                            <ul className="divide-y divide-slate-100">
                                {items.map((it, idx) => {
                                    const Icon = it.icon || ArrowRight;
                                    const isActive = idx === active;
                                    const isCaseSection = it.kind === "case" && idx > 0 && items[idx - 1].kind === "action";
                                    return (
                                        <li key={`${it.kind}-${it.id || it.key || idx}`}>
                                            {isCaseSection && (
                                                <div className="px-3 pt-3 pb-1 font-mono-tr text-[10px] tracking-[0.22em] uppercase text-slate-400">Your recent cases</div>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => pick(it)}
                                                onMouseEnter={() => setActive(idx)}
                                                data-testid={`client-palette-item-${idx}`}
                                                className={`w-full px-4 py-2.5 flex items-center gap-3 text-left transition ${isActive ? "bg-cyan-50/70" : "hover:bg-slate-50"}`}
                                            >
                                                <div className={`w-7 h-7 rounded-md grid place-items-center shrink-0 ${it.kind === "action" ? "text-cyan-700 bg-cyan-50" : "text-indigo-700 bg-indigo-50"}`}>
                                                    <Icon size={13} />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-[13px] text-slate-900 truncate">{it.label}</div>
                                                    <div className="text-[11.5px] text-slate-500 truncate">{it.sublabel}</div>
                                                </div>
                                                {isActive && <ArrowRight size={13} className="text-slate-400" />}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </>
                    )}
                </div>
                <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/40 text-[11px] text-slate-500 flex items-center justify-between">
                    <span><kbd className="font-mono-tr text-[10.5px]">↑</kbd> <kbd className="font-mono-tr text-[10.5px]">↓</kbd> navigate · <kbd className="font-mono-tr text-[10.5px]">↵</kbd> open</span>
                    <span><kbd className="font-mono-tr text-[10.5px]">⌘/Ctrl + K</kbd> anywhere to reopen</span>
                </div>
            </div>
            <style>{`@keyframes tr-ccp-in{from{opacity:0;transform:translateY(-12px) scale(.97)}to{opacity:1;transform:translateY(0) scale(1)}}`}</style>
        </div>
    );
}
